import type { Body } from "./parse";

// A recorded body is text, or base64 of something that was never text, and the two are told apart by what the
// recorder said the encoding and the type were rather than by looking at the bytes.

export interface Shown {
  text: string;
  // What is on screen instead of the body, for a body there is no point in spelling out: a picture, or a megabyte
  // of something binary.
  note: string;
  image: string | null;
  truncated: boolean;
  pretty: boolean;
}

// A response body runs to megabytes, and a card is not where somebody reads one: past this it is cut and says so,
// where a `<Code>` block holding all of it is a page that stutters every time a card is opened.
export const MAX_BODY_CHARS = 20000;

export function readShown(body: Body | null): Shown | null {
  if (!body) return null;

  if (isImage(body)) {
    return {
      text: "",
      note: `${body.mimeType} — shown rather than spelled out`,
      image: dataUri(body),
      truncated: false,
      pretty: false,
    };
  }

  const decoded = decode(body);
  if (decoded === null) {
    return {
      text: "",
      note: `${body.mimeType || "Binary"} — not text, so it is not shown`,
      image: null,
      truncated: false,
      pretty: false,
    };
  }

  const pretty = prettyJson(decoded, body.mimeType);
  const text = pretty ?? decoded;
  return {
    text: text.slice(0, MAX_BODY_CHARS),
    note: "",
    image: null,
    truncated: text.length > MAX_BODY_CHARS,
    pretty: pretty !== null,
  };
}

export function isImage(body: Body): boolean {
  return body.mimeType.startsWith("image/") && !body.mimeType.includes("svg") && body.encoding === "base64"
    && body.text !== "";
}

function dataUri(body: Body): string {
  return `data:${body.mimeType};base64,${body.text}`;
}

// Base64 the recorder wrote round bytes it could not put in JSON; only the ones whose type says they were text are
// turned back, since a decoded PNG is a screenful of replacement characters.
function decode(body: Body): string | null {
  if (body.encoding !== "base64") return body.text;
  if (!looksTextual(body.mimeType)) return null;
  try {
    const binary = atob(body.text);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  } catch {
    return null;
  }
}

function looksTextual(mimeType: string): boolean {
  if (!mimeType) return true;
  return /^text\/|json|xml|javascript|ecmascript|urlencoded|graphql|x-www-form/.test(mimeType);
}

// JSON is written minified by everything that serves it and read by nobody in that shape; anything else is left
// exactly as it was recorded, a body being evidence rather than a document to tidy.
function prettyJson(text: string, mimeType: string): string | null {
  const trimmed = text.trim();
  const looksJson = /json/.test(mimeType) || trimmed.startsWith("{") || trimmed.startsWith("[");
  if (!looksJson || trimmed === "") return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed === null || typeof parsed !== "object") return null;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
}
